// scheduling/components/ClientPYHistogram.jsx
// Per-PY-job weekly work-distribution histogram. Used on ClientView's Prior Year
// tab inside the expanded row. Reuses .idd-hist-* CSS so the visual matches the
// IDD page. Renders one teal bar per week + optional deadline/completion markers.
// Weeks with negative net hours (PE corrections) render below the baseline.

import { useState, useEffect, useMemo } from 'react';
import { fetchJobWeeklyWIP } from '../services/schedData';

const CHART_H = 100;

// Pick how often to show an x-axis label based on week count so labels stay readable
// as the container width spreads across more or fewer weeks.
function labelStride(weekCount) {
  if (weekCount <= 16) return 2;
  if (weekCount <= 32) return 4;
  if (weekCount <= 64) return 8;
  if (weekCount <= 104) return 13;
  return Math.max(13, Math.ceil(weekCount / 8));
}

function getMonday(d) {
  const dt = new Date(d);
  const dow = dt.getDay();
  dt.setDate(dt.getDate() - (dow === 0 ? 6 : dow - 1));
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function addWeeks(d, n) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n * 7);
  return dt;
}

function fmtWeekLabel(d) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Parse 'YYYY-MM-DD' (or SQL DATE ISO) as LOCAL midnight to avoid TZ drift.
function parseLocalDate(val) {
  if (!val) return null;
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

export default function ClientPYHistogram({ peJobIndex, deadline, completionDate, highlightStart, highlightEnd }) {
  const [data, setData] = useState(null); // null=loading, []=empty, [...]=loaded
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!peJobIndex) { setData([]); return; }
    let cancelled = false;
    fetchJobWeeklyWIP(peJobIndex)
      .then(d => { if (!cancelled) setData(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setData([]); });
    return () => { cancelled = true; };
  }, [peJobIndex]);

  const computed = useMemo(() => {
    if (!data || data.length === 0) return null;
    const points = data
      .map(r => {
        const d = parseLocalDate(r.WeekStart);
        return d ? { date: getMonday(d), hours: Number(r.TotalHours) || 0 } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);
    if (!points.length) return null;

    let origin = points[0].date;
    let end = points[points.length - 1].date;
    const dlMon = deadline ? getMonday(parseLocalDate(deadline) || new Date()) : null;
    const cpMon = completionDate ? getMonday(parseLocalDate(completionDate) || new Date()) : null;
    if (dlMon && dlMon < origin) origin = dlMon;
    if (dlMon && dlMon > end) end = dlMon;
    if (cpMon && cpMon < origin) origin = cpMon;
    if (cpMon && cpMon > end) end = cpMon;

    const weekCount = Math.round((end - origin) / (7 * 86400000)) + 1;
    const weeks = [];
    for (let i = 0; i < weekCount; i++) {
      const monday = addWeeks(origin, i);
      const point = points.find(p => Math.abs(p.date - monday) < 86400000);
      weeks.push({ idx: i, monday, hours: point ? point.hours : 0 });
    }

    const maxAbs = Math.max(...weeks.map(w => Math.abs(w.hours)), 0.1);
    const hasNegative = weeks.some(w => w.hours < 0);
    const total = weeks.reduce((s, w) => s + w.hours, 0);
    const deadlineIdx = dlMon ? Math.round((dlMon - origin) / (7 * 86400000)) : null;
    const completionIdx = cpMon ? Math.round((cpMon - origin) / (7 * 86400000)) : null;

    // Optional highlight band (used by ClientView's hover-team-row interaction)
    const hlStartMon = highlightStart ? getMonday(parseLocalDate(highlightStart) || new Date()) : null;
    const hlEndMon   = highlightEnd   ? getMonday(parseLocalDate(highlightEnd)   || new Date()) : null;
    const highlightStartIdx = hlStartMon ? Math.max(0, Math.round((hlStartMon - origin) / (7 * 86400000))) : null;
    const highlightEndIdx   = hlEndMon   ? Math.min(weeks.length - 1, Math.round((hlEndMon - origin) / (7 * 86400000))) : null;

    return { weeks, maxAbs, hasNegative, total, deadlineIdx, completionIdx, highlightStartIdx, highlightEndIdx };
  }, [data, deadline, completionDate, highlightStart, highlightEnd]);

  if (data === null) return <div style={{ padding: 12, fontSize: 11, color: '#9CA3AF' }}>Loading PY work distribution…</div>;
  if (data.length === 0 || !computed) return <div style={{ padding: 12, fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>No PY WIP recorded</div>;

  const { weeks, maxAbs, hasNegative, total, deadlineIdx, completionIdx, highlightStartIdx, highlightEndIdx } = computed;
  const weekCount = weeks.length;
  const colPct = 100 / weekCount;             // each week's column width as % of container
  const barPct = colPct * 0.85;               // bar slimmer than its column
  const barOffsetPct = (colPct - barPct) / 2; // center bar within its column
  const baseline = hasNegative ? CHART_H / 2 : CHART_H - 4;
  const halfH = hasNegative ? (CHART_H / 2 - 4) : (CHART_H - 8);
  const stride = labelStride(weekCount);

  return (
    <div className="idd-hist-root" style={{ borderBottom: 'none', width: '100%' }}>
      <div className="idd-hist-body" style={{ width: '100%', height: CHART_H + 22, position: 'relative', overflow: 'hidden' }}>
        {/* Gridlines: thin every-week ticks when sparse, otherwise only on labeled weeks */}
        {weeks.map(w => (weekCount > 80 ? (w.idx % stride === 0) : true) ? (
          <div key={`g-${w.idx}`} className="idd-hist-gridline" style={{ left: `${w.idx * colPct}%`, height: CHART_H }} />
        ) : null)}
        {highlightStartIdx !== null && highlightEndIdx !== null && highlightEndIdx >= highlightStartIdx && (
          <div
            style={{
              position: 'absolute',
              left: `${highlightStartIdx * colPct}%`,
              width: `${(highlightEndIdx - highlightStartIdx + 1) * colPct}%`,
              top: 0,
              height: CHART_H,
              background: 'rgba(245, 158, 11, 0.18)',
              borderLeft: '2px dashed #F59E0B',
              borderRight: '2px dashed #F59E0B',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}
        {hasNegative && (
          <div style={{ position: 'absolute', left: 0, right: 0, top: baseline, height: 1, background: '#9CA3AF', pointerEvents: 'none' }} />
        )}
        {weeks.map(w => {
          if (w.hours === 0) return null;
          const isNeg = w.hours < 0;
          const barH = Math.max(2, Math.abs(w.hours) / maxAbs * halfH);
          const top = isNeg ? baseline : baseline - barH;
          return (
            <div key={`b-${w.idx}`}
              className="idd-hist-bar"
              style={{
                left: `${w.idx * colPct + barOffsetPct}%`,
                top,
                width: `${barPct}%`,
                minWidth: 2,
                height: barH,
                background: isNeg ? '#FCA5A5' : '#003D4C',
                opacity: 0.85,
              }}
              onMouseEnter={e => setTooltip({
                x: e.clientX + 12,
                y: e.clientY - 56,
                lines: [
                  `Week of ${fmtWeekLabel(w.monday)}`,
                  `${isNeg ? 'Net correction: ' : ''}${w.hours.toFixed(2)}h`,
                ],
              })}
              onMouseLeave={() => setTooltip(null)}
            />
          );
        })}
        {deadlineIdx !== null && deadlineIdx >= 0 && deadlineIdx < weekCount && (
          <div title="Internal Due Date"
            style={{ position: 'absolute', left: `calc(${(deadlineIdx + 0.5) * colPct}% - 1px)`, top: 0, height: CHART_H, width: 0, borderLeft: '2px dashed #F59E0B', pointerEvents: 'none' }} />
        )}
        {completionIdx !== null && completionIdx >= 0 && completionIdx < weekCount && (
          <div title="Effective Completed"
            style={{ position: 'absolute', left: `calc(${(completionIdx + 0.5) * colPct}% - 1px)`, top: 0, height: CHART_H, width: 0, borderLeft: '2px dashed #10B981', pointerEvents: 'none' }} />
        )}
        {weeks.map(w => (w.idx % stride === 0) ? (
          <div key={`l-${w.idx}`} style={{ position: 'absolute', left: `${w.idx * colPct}%`, top: CHART_H + 2, fontSize: 9, color: '#9CA3AF', width: `${colPct * stride}%`, textAlign: 'left', paddingLeft: 2, pointerEvents: 'none' }}>
            {fmtWeekLabel(w.monday)}
          </div>
        ) : null)}
      </div>
      <div className="idd-hist-legend" style={{ marginTop: 2 }}>
        <span className="idd-hist-legend-item">
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#003D4C' }} />
          Weekly WIP
        </span>
        {hasNegative && (
          <span className="idd-hist-legend-item">
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#FCA5A5' }} />
            Net correction
          </span>
        )}
        {deadline && deadlineIdx !== null && (
          <span className="idd-hist-legend-item">
            <span style={{ display: 'inline-block', width: 12, height: 2, background: '#F59E0B' }} />
            Internal Due Date
          </span>
        )}
        {completionDate && completionIdx !== null && (
          <span className="idd-hist-legend-item">
            <span style={{ display: 'inline-block', width: 12, height: 2, background: '#10B981' }} />
            Effective Completed
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#374151' }}>Total: {total.toFixed(2)}h</span>
      </div>
      {tooltip && (
        <div style={{ position: 'fixed', left: tooltip.x, top: tooltip.y, background: '#1F2937', color: '#fff', padding: '6px 8px', borderRadius: 4, fontSize: 11, lineHeight: 1.4, whiteSpace: 'pre-line', zIndex: 1000, pointerEvents: 'none' }}>
          {tooltip.lines.join('\n')}
        </div>
      )}
    </div>
  );
}
